import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription, combineLatest } from 'rxjs';
import { MachiningJournalService } from './machining-journal.service';
import { MachinePhoto, JournalEntry, JournalMachine } from './machining-journal.model';

type GalleryFilter = 'all' | 'lathe' | 'mill';

@Component({
    selector: 'app-machining-gallery',
    templateUrl: './machining-gallery.component.html',
    styleUrls: ['./machining-gallery.component.scss'],
    standalone: false
})
export class MachiningGalleryComponent implements OnInit, OnDestroy {
  photos: MachinePhoto[] = [];
  entriesById: Record<string, JournalEntry> = {};
  filter: GalleryFilter = 'all';
  userId: string | null = null;
  loading = true;

  lightboxPhoto: MachinePhoto | null = null;
  lightboxIndex = 0;

  private authSub?: Subscription;
  private dataSub?: Subscription;

  constructor(
    private auth: AngularFireAuth,
    private journalService: MachiningJournalService
  ) {}

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadData();
      } else {
        this.loading = false;
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.dataSub?.unsubscribe();
  }

  private loadData() {
    if (!this.userId) return;
    this.dataSub = combineLatest([
      this.journalService.getAllPhotos(this.userId),
      this.journalService.getEntries(this.userId)
    ]).subscribe(([photos, entries]) => {
      this.photos = photos;
      this.entriesById = {};
      entries.forEach(e => { this.entriesById[e.id] = e; });
      this.loading = false;
    });
  }

  setFilter(f: GalleryFilter) {
    this.filter = f;
    if (this.lightboxPhoto && !this.filteredPhotos.find(p => p.id === this.lightboxPhoto!.id)) {
      this.closeLightbox();
    }
  }

  get filteredPhotos(): MachinePhoto[] {
    if (this.filter === 'all') return this.photos;
    return this.photos.filter(p => {
      const m: JournalMachine | undefined = this.entriesById[p.journalEntryId]?.machine;
      if (!m) return false;
      if (m === 'both') return true;
      return m === this.filter;
    });
  }

  formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  openLightbox(photo: MachinePhoto) {
    this.lightboxPhoto = photo;
    this.lightboxIndex = this.filteredPhotos.findIndex(p => p.id === photo.id);
  }

  closeLightbox() {
    this.lightboxPhoto = null;
  }

  prevPhoto() {
    if (this.lightboxIndex > 0) {
      this.lightboxIndex--;
      this.lightboxPhoto = this.filteredPhotos[this.lightboxIndex];
    }
  }

  nextPhoto() {
    if (this.lightboxIndex < this.filteredPhotos.length - 1) {
      this.lightboxIndex++;
      this.lightboxPhoto = this.filteredPhotos[this.lightboxIndex];
    }
  }

  async deletePhoto(photo: MachinePhoto) {
    if (!this.userId || !confirm('Delete this photo?')) return;
    if (this.lightboxPhoto?.id === photo.id) this.closeLightbox();
    await this.journalService.deletePhoto(this.userId, photo);
  }
}
